import type { ReactNode } from 'react';
import { Link, useLocation } from 'wouter';
import { STANDARD_OBJECT_TYPES } from '../objectTypes';

export function Layout({ children }: { children: ReactNode }) {
  const [location] = useLocation();

  return (
    <div className="min-h-screen flex">
      <Sidebar location={location} />
      <main className="flex-1 min-w-0 overflow-x-hidden">{children}</main>
    </div>
  );
}

function Sidebar({ location }: { location: string }) {
  return (
    <aside className="w-60 shrink-0 border-r border-border bg-panel flex flex-col">
      <div className="px-4 py-4 border-b border-border">
        <Link href="/" className="flex items-center gap-2 text-text-primary hover:no-underline">
          <span
            className="w-7 h-7 rounded-md bg-accent text-bg flex items-center justify-center font-bold text-sm"
            aria-hidden
          >
            H
          </span>
          <span className="font-semibold">TheHub</span>
        </Link>
      </div>

      <nav className="flex-1 px-2 py-3 overflow-y-auto">
        <NavSection label="Workspace">
          <NavLink href="/" active={location === '/'} icon="🏠">
            Dashboard
          </NavLink>
        </NavSection>

        <NavSection label="CRM">
          {STANDARD_OBJECT_TYPES.map((t) => (
            <NavLink
              key={t.id}
              href={`/objects/${t.id}`}
              active={location.startsWith(`/objects/${t.id}`)}
              icon={t.icon}
            >
              {t.label_plural}
            </NavLink>
          ))}
        </NavSection>

        <NavSection label="Marketing">
          <NavLink href="/forms" active={location.startsWith('/forms')} icon="📝">
            Forms
          </NavLink>
          <NavLink href="/emails" active={false} icon="✉️" disabled>
            Emails
          </NavLink>
          <NavLink href="/landing" active={false} icon="🌐" disabled>
            Landing Pages
          </NavLink>
        </NavSection>

        <NavSection label="Automation">
          <NavLink href="/workflows" active={false} icon="⚙️" disabled>
            Workflows
          </NavLink>
          <NavLink href="/sequences" active={false} icon="📤" disabled>
            Sequences
          </NavLink>
        </NavSection>

        <NavSection label="AI">
          <NavLink href="/copilot" active={false} icon="🤖" disabled>
            Copilot
          </NavLink>
          <NavLink href="/ad-manager" active={false} icon="📈" disabled>
            Ad Manager
          </NavLink>
        </NavSection>

        <NavSection label="Reports">
          <NavLink href="/dashboards" active={false} icon="📊" disabled>
            Dashboards
          </NavLink>
        </NavSection>
      </nav>

      <div className="border-t border-border px-2 py-2">
        <NavLink href="/settings" active={location === '/settings'} icon="⚙️">
          Settings
        </NavLink>
      </div>
    </aside>
  );
}

function NavSection({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="mb-4">
      <div className="px-3 py-1 text-[10px] uppercase tracking-wider text-text-dim font-semibold">
        {label}
      </div>
      <div className="flex flex-col">{children}</div>
    </div>
  );
}

function NavLink({
  href,
  active,
  icon,
  children,
  disabled,
}: {
  href: string;
  active: boolean;
  icon: string;
  children: ReactNode;
  disabled?: boolean;
}) {
  if (disabled) {
    return (
      <span
        className="flex items-center gap-2 px-3 py-1.5 rounded-md text-sm text-text-dim cursor-not-allowed"
        title="Coming soon"
      >
        <span className="w-4 text-center">{icon}</span>
        <span>{children}</span>
        <span className="ml-auto text-[9px] uppercase tracking-wider opacity-70">soon</span>
      </span>
    );
  }
  return (
    <Link
      href={href}
      className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm transition-colors ${
        active
          ? 'bg-accent-subtle text-accent'
          : 'text-text-primary hover:bg-panel-2'
      }`}
    >
      <span className="w-4 text-center">{icon}</span>
      <span>{children}</span>
    </Link>
  );
}
